'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { Box, Text } from '@highlight-cp/core-ui';

import PromptPill from './prompt-pill';
import classes from './prompt-pills.module.css';

const DEFAULT_LIST_ARIA_LABEL = 'Suggestions';

export interface PromptPillsProps {
  questions: string[];
  onQuestionSelect: (question: string, index: number) => void;
  disabled?: boolean;
  /** Optional heading above the pills. Omit, pass `null`, or pass `''` to hide the prompt line. */
  promptLine?: string | null;
  /** Accessible name for the pill list when the prompt line is hidden. Defaults to "Suggestions". */
  listAriaLabel?: string;
}

/**
 * Optional prompt line with right-aligned `PromptPill` controls for questions or statements.
 */
const PromptPills = ({
  questions,
  onQuestionSelect,
  disabled = false,
  promptLine,
  listAriaLabel = DEFAULT_LIST_ARIA_LABEL,
}: PromptPillsProps) => {
  const promptId = useId();
  const listId = useId();
  const pillButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  useEffect(() => {
    setFocusedIndex((currentIndex) => Math.min(currentIndex, Math.max(questions.length - 1, 0)));
  }, [questions.length]);

  const focusPill = useCallback(
    (index: number) => {
      const nextIndex = Math.max(0, Math.min(index, questions.length - 1));
      setFocusedIndex(nextIndex);
      pillButtonRefs.current[nextIndex]?.focus();
    },
    [questions.length],
  );

  const handleListKeyDown = useCallback(
    (event: KeyboardEvent<HTMLUListElement>) => {
      if (disabled || questions.length <= 1) {
        return;
      }

      let nextIndex: number | null = null;

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          nextIndex = focusedIndex + 1;
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          nextIndex = focusedIndex - 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = questions.length - 1;
          break;
        default:
          return;
      }

      if (nextIndex < 0 || nextIndex >= questions.length) {
        return;
      }

      event.preventDefault();
      focusPill(nextIndex);
    },
    [disabled, focusPill, focusedIndex, questions.length],
  );

  if (questions.length === 0) {
    return null;
  }

  const showPromptLine = Boolean(promptLine?.trim());

  return (
    <Box className={classes.root}>
      {showPromptLine ? (
        <Box className={classes.promptLine}>
          <Text id={promptId} component="p">
            {/* TODO(EPD-1096): Prefer Text color/`c` variants once gray.7 is supported */}
            <span className={classes.promptLineText}>{promptLine}</span>
          </Text>
        </Box>
      ) : null}
      <Box
        component="ul"
        id={listId}
        className={classes.pills}
        aria-labelledby={showPromptLine ? promptId : undefined}
        aria-label={showPromptLine ? undefined : listAriaLabel}
        onKeyDown={handleListKeyDown}
      >
        {questions.map((question, index) => (
          <Box component="li" key={`${index}-${question}`} className={classes.pillItem}>
            <PromptPill
              ref={(element) => {
                pillButtonRefs.current[index] = element;
              }}
              label={question}
              disabled={disabled}
              tabIndex={index === focusedIndex ? 0 : -1}
              onClick={() => onQuestionSelect(question, index)}
              onFocus={() => setFocusedIndex(index)}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PromptPills;
